import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient, type QueryKey } from "@tanstack/react-query";
import { toast } from "sonner";
import { CircleX, HelpCircle } from "lucide-react";
import Dialog from "components/shared/Dialog";
import Button from "components/shared/Button";
import BaseForm, { Field } from "components/shared/Form";
import { Textarea } from "components/ui/textarea";
import useApi from "utils/api";
import { useVisible } from "utils/hooks/useVisible";
import { getErrorMessage } from "utils/helpers/errors";
import { withLoadingToast } from "../../../Component/helpers/withLoadingToast";

interface UseCancelDialogProps {
    queryOptions: { queryKey: QueryKey };
    onSuccess?: () => void;
}

type CancelFormValues = {
    reason: string;
};

export const useCancelDialog = ({ queryOptions, onSuccess }: UseCancelDialogProps) => {
    const { t } = useTranslation();
    const { authPut } = useApi();
    const queryClient = useQueryClient();
    const { open, visible, close, selected } = useVisible();

    const form = useForm<CancelFormValues>({
        defaultValues: { reason: "" },
    });

    const cancelMutate = useMutation({
        ...withLoadingToast(t("cancelling")),
        mutationFn: ({ id, reason }: { id: string; reason: string }) =>
            authPut(`/request/${id}/cancel`, reason ? { data: { reason } } : undefined),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryOptions.queryKey });
            onSuccess?.();
            toast.success(t("request-cancelled-successfully"));
            form.reset();
            close();
        },
        onError: (error) => {
            getErrorMessage(error, t);
            close();
        },
    });

    const onFinish = useCallback((value: CancelFormValues) => {
        cancelMutate.mutate({ id: selected?.id, reason: value.reason });
    }, [selected, cancelMutate]);

    const onClose = useCallback(() => {
        form.reset();
        close();
    }, [close, form]);

    const dialog = useMemo(
        () => (
            <Dialog open={visible} close={onClose} size="lg">
                <div className="tw-flex tw-flex-col tw-items-center tw-text-center tw-mb-5">
                    <div className="tw-w-12 tw-h-12 tw-rounded-full tw-bg-red-50 tw-flex tw-items-center tw-justify-center tw-mb-3">
                        <HelpCircle size={22} className="tw-text-red-500" />
                    </div>
                    <h2 className="tw-text-sm tw-font-semibold">{t("do-you-want-to-cancel-request")}</h2>
                </div>

                <BaseForm form={form} onFinish={onFinish}>
                    <Field name="reason" label={t("reason")}>
                        <Textarea rows={5} className="tw-resize-none field-sizing-fixed" placeholder={t("reason")} />
                    </Field>

                    <div className="tw-flex tw-justify-end tw-gap-2 tw-pt-2">
                        <Button type="button" variant="outline" onClick={onClose} disabled={cancelMutate.isPending}>
                            <CircleX />
                            {t("cancel")}
                        </Button>

                        <Button
                            type="submit"
                            variant="destructive"
                            loading={cancelMutate.isPending}
                            disabled={cancelMutate.isPending}
                        >
                            {t("confirm")}
                        </Button>
                    </div>
                </BaseForm>
            </Dialog>
        ),
        [form, visible, onClose, onFinish, t, cancelMutate.isPending],
    );

    return { open, dialog };
};
